import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { CartResponse as CartV2Response } from 'lib/commerce/cart/types';
import {
  CartContextType,
  CartFlow,
  CartFlowScreens,
  ProductType,
} from './types';

// Per-store translation key for tax display (resolved to localized string at render time)
// @deprecated alias retained so legacy "TaxConfig" references (e.g. add-country-locale script) still resolve.
export const TaxConfig = {
  [ShopifyStore.IN]: 'Tax incl',
  [ShopifyStore.US]: 'Tax excl',
  [ShopifyStore.EU]: 'Tax incl',
  [ShopifyStore.GB]: 'Tax incl',
  [ShopifyStore.AE]: 'Tax excl',
  [ShopifyStore.ROW]: 'Tax excl',
  [ShopifyStore.CA]: 'Tax excl',
  [ShopifyStore.SA]: 'Tax incl',
  [ShopifyStore.MX]: 'Tax incl',
  [ShopifyStore.AU]: 'Tax incl',
  [ShopifyStore.ZA]: 'Tax incl',
  [ShopifyStore.JP]: 'Tax incl',
};

export const TaxConfigKey = {
  [ShopifyStore.IN]: 'sharedCart.cartUi.taxIncl',
  [ShopifyStore.US]: 'sharedCart.cartUi.taxExcl',
  [ShopifyStore.EU]: 'sharedCart.cartUi.taxIncl',
  [ShopifyStore.GB]: 'sharedCart.cartUi.taxIncl',
  [ShopifyStore.AE]: 'sharedCart.cartUi.taxExcl',
  [ShopifyStore.ROW]: 'sharedCart.cartUi.taxExcl',
  [ShopifyStore.CA]: 'sharedCart.cartUi.taxExcl',
  [ShopifyStore.SA]: 'sharedCart.cartUi.taxIncl',
  [ShopifyStore.MX]: 'sharedCart.cartUi.taxIncl',
  [ShopifyStore.AU]: 'sharedCart.cartUi.taxIncl',
  [ShopifyStore.ZA]: 'sharedCart.cartUi.taxIncl',
  [ShopifyStore.JP]: 'sharedCart.cartUi.taxIncl',
};

/**
 * @deprecated Use TaxConfigKey + t() instead. Kept for backward compat during migration.
 * Resolves translated tax string via a provided t() function from useTranslations('cart').
 */
export const getTaxString = (
  store: ShopifyStore | null | undefined,
  t: (key: string) => string,
): string => {
  if (!store) return '';
  const key = TaxConfigKey[store as ShopifyStore];
  return key ? t(key) : '';
};

export type StoreApiResult<T = unknown> =
  | { error: true; status?: number; message?: string }
  | (T & { error?: false });

/** Type guard: true when the result is a successful cart response (not an error). */
export function isCartApiSuccess(
  r: StoreApiResult<{ cart?: unknown }> | undefined,
): r is { cart: Record<string, unknown> | null } {
  return !!r && !('error' in r && r.error) && 'cart' in r;
}

export const makeStoreApiRequest = async (
  store: ShopifyStore | undefined,
  url: string,
  body?: Record<string, unknown>,
): Promise<StoreApiResult<{ cart?: unknown; [key: string]: unknown }>> => {
  if (store === undefined) {
    return { error: true, message: 'Missing store' };
  }
  const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}store=${store}`;
  try {
    const response = await fetch(fetchUrl, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      console.error(
        `[Cart API] ${response.status} ${response.statusText} for ${fetchUrl}`,
      );
      return { error: true, status: response.status };
    }
    return (await response.json()) as StoreApiResult;
  } catch (e) {
    console.error('[Cart API] Network error:', e);
    return {
      error: true,
      message: e instanceof Error ? e.message : String(e),
    };
  }
};

/** Strip empty block elements from Shopify product description HTML. */
export const getSanitizedProductDescriptionHtml = (
  html: string | null | undefined,
): string => {
  if (!html) return '';

  return html
    .replace(/<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
    .replace(/<div[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/div>/gi, '')
    .trim();
};

export const hasHtmlTextContent = (html: string | null | undefined): boolean => {
  if (!html) return false;

  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return text.length > 0;
};

export const getFromLocalStorage = (key: string) => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(key);
};

export const deleteFromLocalStorage = (key: string) => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(key);
};

export const saveToLocalStorage = (key: string, value: string) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, value);
};

export const HeroCategoriesMap = {
  ATHLETES: 'athletes',
  HEALTHCARE: 'healthcare-workers',
  SCIENTIST: 'scientist-and-researchers',
  VETERANS: 'veterans',
  EDUCATION: 'education',
};

export const heroDiscountCodes: Record<string, string> = {
  [HeroCategoriesMap.ATHLETES]: 'UHHROATHL15',
  [HeroCategoriesMap.HEALTHCARE]: 'UHHROHLT15',
  [HeroCategoriesMap.SCIENTIST]: 'UHHROSCRS15',
  [HeroCategoriesMap.VETERANS]: 'UHHROVET15',
  [HeroCategoriesMap.EDUCATION]: 'UHHROSTD15',
};

export const getHeroDiscountCode = (
  category: string,
): string | undefined => {
  return heroDiscountCodes[category];
};

export const getHeroDiscountPercentage = (): number => 0.15;

export const defaultEmptyDiscountConfig = {
  percentage: 0,
  amount: 0,
  discountCode: '',
  type: 'amount',
  code: '',
};

export const initialCartContextValue: CartContextType = {
  cart: null,
  cartHydrationState: 'pending',
  store: null,
  currency: null,
  taxes: 0,
  duty: 0,
  setStore: () => undefined,
  isCartVisible: false,
  showCart: () => undefined,
  hideCart: () => undefined,

  // Upsell modal state
  isUpsellVisible: false,
  showUpsellModal: () => undefined,
  hideUpsellModal: () => undefined,
  proceedWithUpsell: async () => undefined,
  addToCartSilently: async () => undefined,

  createCartWithItem: async () => false,
  addToCart: async () => undefined,
  updateCartQuantity: async () => undefined,
  incrementCartQuantity: async () => undefined,
  addToCartByShopifyVariantId: async () => undefined,
  removeFromCart: async () => undefined,
  deleteFromCart: async () => undefined,
  checkout: () => undefined,
  updateCartAttributes: async () => undefined,
  altContent: null,
  setAltContent: () => undefined,
  referralDiscount: 0,
  ultrahumanXAdded: null,
  deleteCartFromLocalStorage: () => undefined,
  affiliateDetails: null,
  campaignActive: false,

  // Campaign data
  campaigns: [],
  sddDelivery: null,
  refetchCampaigns: () => undefined,
  campaignsLoading: false,
  campaignsError: null,
  getCampaignForProduct: () => undefined,

  cartFlow: CartFlow.OG,
  setCartFlow: () => undefined,
  cartLoading: false,

  activeFlowScreen: CartFlowScreens.ITEM_LIST,
  setActiveFlowScreen: () => undefined,

  getCartAttributeValue: () => undefined,
  getCartAttributeIndex: () => undefined,

  error: null,
  setError: () => undefined,
  applicableDiscountCoupons: [],
  getExpressCartInput: () => ({ attributes: [], discountCodes: [] }),
  referral: '',
  taxString: '',
  hasOnlyNonStandaloneItems: false,
  cartType: ProductType.GENERIC,
};

export type CartMutateResponse = StoreApiResult<CartV2Response>;

/** True when a mutate response is a typed cart, not an error. */
export function isCartMutateSuccess(
  r: CartMutateResponse | null | undefined,
): r is CartV2Response {
  return !!r && !('error' in r && r.error) && 'cart' in r;
}

export type CartErrorClass =
  | 'none'
  | 'transport'
  | 'state'
  | 'validation'
  | 'discount';

/** Classifies a mutate response for re-read/toast decisions. */
export function classifyCartError(
  response: CartMutateResponse | null | undefined,
): CartErrorClass {
  if (!response) return 'transport';
  if ('error' in response && response.error) {
    return 'status' in response && response.status === 400
      ? 'validation'
      : 'transport';
  }
  const failedStep = 'failedStep' in response ? response.failedStep : undefined;
  const userErrors = 'userErrors' in response ? response.userErrors ?? [] : [];
  if (!failedStep && userErrors.length === 0) return 'none';
  if (failedStep === 'transport') return 'transport';
  if (failedStep === 'discount') return 'discount';
  return 'state';
}

export const shouldRereadCartError = (errorClass: CartErrorClass): boolean =>
  errorClass === 'transport' ||
  errorClass === 'state' ||
  errorClass === 'discount';

export const shouldToastCartError = (errorClass: CartErrorClass): boolean =>
  errorClass === 'transport' ||
  errorClass === 'state' ||
  errorClass === 'validation';

/** Maps a failed response to friendly toast copy (never raw Shopify text). */
export function getCartApiErrorMessage(
  response: CartMutateResponse | null | undefined,
  fallback = 'Unable to update cart. Please try again.',
): string {
  const failedStep =
    response && 'failedStep' in response ? response.failedStep : undefined;
  switch (failedStep) {
    case 'add':
    case 'create':
      return 'Failed to add item to cart. Please try again.';
    case 'remove':
      return 'Unable to remove item. Please try again.';
    case 'modify':
      return 'Unable to update quantity. Please try again.';
    default:
      return fallback;
  }
}

/** Resolves to `null` if the promise doesn't settle within `ms`. */
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve(null);
      }
    }, ms);
    const finish = (value: T | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    promise.then(finish, () => finish(null));
  });
}

